import React, { FC, useEffect, useState } from 'react';
import { Button, Menu, Icon }             from 'semantic-ui-react';
import type { SemanticICONS }             from 'semantic-ui-react';
import { useCentralCalls }                from '@src/rest/CentralCalls';
import { useCurrentUser, useSystemModals, useFeatureFlags } from '@src/hooks';
import { DashPage }                       from './DashContainerPage';
import { CreateDashPageDialog } from './operations/CreateDashPageDialog';
import { DeleteDashPageDialog } from './operations/DeleteDashPageDialog';
import { Flexbox, FlexItem, StyledContainer } from '@src/views/containers';
import { FlexAlign, FlexJustify, Position, Size, CorvalUIColor } from '@src/types';

interface DashSidebarProps {
    activeView?: string;
    setActiveView: (view: DashPage) => void;
    isCollapsed: boolean;
    toggleSideBar: () => void;
    editMode: boolean;
    toggleEditMode: () => void;
    toggleDeletePage: (pageId: string) => void;
    onNewPage: () => void;
}

const DashSidebar: FC<DashSidebarProps> = ({
    setActiveView,
    isCollapsed,
    toggleSideBar,
    editMode,
    toggleEditMode,
    toggleDeletePage,
    onNewPage
}) => {
    const [dashPages, setDashPages] = useState<DashPage[]>([]);
    const [currentDashPage, setCurrentDashPage] = useState<DashPage | null>(null);
    const [refreshKey, setRefreshKey] = useState<Boolean>(false);
    const { TableSelect } = useCentralCalls();
    const { currentUser } = useCurrentUser();
    const modalActions = useSystemModals();
    const { enableReportsAdmin } = useFeatureFlags();

    const fetchDashPageData = async () => {
        try {
            const data: DashPage[] = await TableSelect('DashPage', { userId: currentUser.id, Hidden: false });
            setDashPages(data);

            // If the current page was deleted, select the first available page
            if (currentDashPage && !data.find(p => p.id === currentDashPage.id)) {
                setCurrentDashPage(data[0]);
                setActiveView(data[0]);
                return;
            }

            // Handle URL parameter and initial page selection
            const params = new URLSearchParams(location.search);
            const dashReportId = params.get('dashReport');
            if (dashReportId) {
                const selectedPage = data.find(page => page.id === dashReportId);
                setCurrentDashPage(selectedPage || data[0]);
                setActiveView(selectedPage || data[0]);
            } else if (!currentDashPage) {
                setCurrentDashPage(data[0]);
                setActiveView(data[0]);
            }
        } catch (error) {
            console.error('Error fetching DashPage data:', error);
        }
    };

    const ChangeDashPage = async (page: DashPage) => {
        setActiveView(page);
        setCurrentDashPage(page);
    };

    const handleNewPage = () => {
        modalActions.show(
            <CreateDashPageDialog
                onPageCreated={() => {
                    setRefreshKey(!refreshKey);
                }}
                onClose={() => modalActions.toggle(false)}
            />,
            {
                size: 'tiny',
                centered: false
            }
        );
    };

    const handleDeletePage = (page: DashPage) => {
        modalActions.show(
            <DeleteDashPageDialog
                page={page}
                onConfirm={async (pageId) => {
                    try {
                        await toggleDeletePage(pageId);
                        setRefreshKey(!refreshKey);
                        modalActions.toggle(false);
                    } catch (error) {
                        console.error('Error deleting page:', error);
                    }
                }}
                onClose={() => modalActions.toggle(false)}
            />,
            {
                size: 'tiny',
                centered: false
            }
        );
    };

    useEffect(() => {
        fetchDashPageData();
    }, [refreshKey, currentUser.id]);

    return (
        <StyledContainer
            className="sidebar-container"
            bgColor={CorvalUIColor.WHITE}
            position={Position.RELATIVE}
        >
            <StyledContainer
                position={Position.STICKY}
                top={0}
                zIndex={200}
                className="sidebar-header"
                bgColor={CorvalUIColor.WHITE}
                borderSize={[null, null, Size.SM, null]}
                borderColor={CorvalUIColor.DK_SCREEN_25}
            >
                <Flexbox justify={FlexJustify.END} className="sidebar-toggle-container">
                    <FlexItem>
                        {!isCollapsed && enableReportsAdmin && (
                            <>
                                <Button
                                    icon={editMode ? 'checkmark' : 'pencil' as SemanticICONS}
                                    size="mini"
                                    color={editMode ? 'green' : undefined}
                                    basic
                                    onClick={toggleEditMode}
                                    className="sidebar-edit-btn"
                                />
                                <Button
                                    icon='plus'
                                    size="mini"
                                    color='black'
                                    basic
                                    onClick={handleNewPage}
                                    className="sidebar-edit-btn"
                                />
                            </>
                        )}
                        <Button
                            icon={(isCollapsed ? 'angle right' : 'angle left') as SemanticICONS}
                            size="mini"
                            basic
                            onClick={toggleSideBar}
                            className="sidebar-toggle-btn-inner"
                        />
                    </FlexItem>
                </Flexbox>
            </StyledContainer>

            <StyledContainer className="sidebar-content">
                <Menu fluid vertical size={'small'} secondary className="sidebar-menu">
                    {dashPages.map((page) => (
                        <Menu.Item
                            key={page.id}
                            active={currentDashPage?.id === page.id}
                            className={`menu-item ${currentDashPage?.id === page.id ? 'active' : ''}`}
                            onClick={() => ChangeDashPage(page)}
                        >
                            <Flexbox justify={FlexJustify.START} align={FlexAlign.CENTER} className="menu-item-inner">
                                <Icon name={(page.icon || 'file text') as SemanticICONS} />
                                {!isCollapsed && (
                                    <>
                                        <span className="menu-item-text">{page.name}</span>
                                        {editMode && enableReportsAdmin && (
                                            <Icon
                                                name='trash'
                                                className="menu-item-delete"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeletePage(page);
                                                }}
                                            />
                                        )}
                                    </>
                                )}
                            </Flexbox>
                        </Menu.Item>
                    ))}
                </Menu>
            </StyledContainer>
        </StyledContainer>
    );
};

export default DashSidebar;
